import { useEffect, useRef } from 'react';
import { useNotificationHub } from './useNotificationHub';
import { getStoredAccessToken } from '../services/apiClient';
import { getSupportDeskActivity } from '../services/supportDeskApi';

const POLL_INTERVAL_MS = 8_000;
const TICKET_NOTIFICATION_TYPE = 'customer-success.ticket';

type UseSupportDeskLiveUpdatesOptions = {
  enabled: boolean;
  onRefresh: () => void;
};

/**
 * Atualiza a mesa quando há mudança nos tickets (poll do watermark)
 * ou notificação SignalR de ticket.
 */
export function useSupportDeskLiveUpdates({
  enabled,
  onRefresh,
}: UseSupportDeskLiveUpdatesOptions): void {
  const onRefreshRef = useRef(onRefresh);
  const lastChangedAtRef = useRef<string | null>(null);
  const refreshingRef = useRef(false);

  onRefreshRef.current = onRefresh;

  const triggerRefresh = (): void => {
    if (refreshingRef.current) {
      return;
    }
    refreshingRef.current = true;
    try {
      onRefreshRef.current();
    } finally {
      window.setTimeout(() => {
        refreshingRef.current = false;
      }, 750);
    }
  };

  useNotificationHub({
    enabled,
    onNotification: (payload) => {
      if (payload.type === TICKET_NOTIFICATION_TYPE) {
        triggerRefresh();
      }
    },
  });

  useEffect(() => {
    if (!enabled || !getStoredAccessToken()) {
      return;
    }

    let cancelled = false;
    let timerId = 0;

    const checkActivity = async (): Promise<void> => {
      if (cancelled || document.visibilityState === 'hidden') {
        return;
      }

      try {
        const activity = await getSupportDeskActivity();
        if (cancelled) {
          return;
        }

        const previous = lastChangedAtRef.current;
        lastChangedAtRef.current = activity.changedAtUtc;
        if (previous && previous !== activity.changedAtUtc) {
          triggerRefresh();
        }
      } catch {
        // Mantém o último watermark; tenta de novo no próximo ciclo.
      }
    };

    const schedule = (): void => {
      window.clearInterval(timerId);
      if (document.visibilityState === 'visible') {
        void checkActivity();
        timerId = window.setInterval(() => {
          void checkActivity();
        }, POLL_INTERVAL_MS);
      }
    };

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        void checkActivity();
      }
      schedule();
    };

    schedule();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(timerId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled]);
}
